import { useMemo } from 'react';
import { useThemeMode } from '../../../theme/ThemeModeProvider';
import { getChartColors } from './chartPalette';

/**
 * The chart ramp, re-resolved when the theme changes.
 *
 * `getChartColors` reads the custom properties through `getComputedStyle`, which is right — a
 * literal would drift from the palette — but it resolves at CALL time, so a component that does not
 * re-render keeps the colours of whichever theme was live when it last did. Charts are exactly that
 * kind of component: they render once and sit there.
 *
 * Keying the memo on the mode is the whole fix. It costs nothing and it is the difference between
 * a theme switch recolouring the charts and a theme switch recolouring everything except them.
 */
export function useChartColors(count?: number): string[] {
  const { mode } = useThemeMode();
  /*
   * `mode` is a dependency the rule cannot see, and it is the only one that matters.
   *
   * getChartColors reads the resolved custom properties off the document, so its result depends on
   * the theme without the theme ever being passed to it. exhaustive-deps models a dependency as
   * "referenced in the callback", which this is not; removing it would make the memo permanent and
   * freeze the charts on whichever palette was live at first render.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getChartColors(count), [mode, count]);
}
